import React from 'react';
import { Alert, Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography } from 'antd';

import { platformOperationalEventsService } from '../../services/api';

const { Title, Text } = Typography;

const SEVERITY_OPTIONS = [
  { label: 'INFO', value: 'INFO' },
  { label: 'WARNING', value: 'WARNING' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'CRITICAL', value: 'CRITICAL' },
];

const severityColor = {
  INFO: 'blue',
  WARNING: 'orange',
  ERROR: 'red',
  CRITICAL: 'magenta',
};

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const PlatformOperationalEventsPage = () => {
  const [filters, setFilters] = React.useState({
    tenant_id: '',
    event_type: '',
    event_code: '',
    severity: undefined,
    source: '',
    correlation_id: '',
    range: null,
    limit: 200,
  });
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const fetchEvents = async () => {
    setLoading(true);
    setError('');

    const params = {
      tenant_id: (filters.tenant_id || '').trim() || undefined,
      event_type: (filters.event_type || '').trim() || undefined,
      event_code: (filters.event_code || '').trim() || undefined,
      severity: filters.severity || undefined,
      source: (filters.source || '').trim() || undefined,
      correlation_id: (filters.correlation_id || '').trim() || undefined,
      from_date: filters.range?.[0] ? filters.range[0].startOf('day').toISOString() : undefined,
      to_date: filters.range?.[1] ? filters.range[1].endOf('day').toISOString() : undefined,
      limit: Number(filters.limit) || 200,
    };

    try {
      const response = await platformOperationalEventsService.list(params);
      setItems(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Nao foi possivel consultar eventos operacionais.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);

  const columns = [
    {
      title: 'Occurred At',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (value) => <Tag color={severityColor[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: 'Event Code',
      dataIndex: 'event_code',
      key: 'event_code',
      render: (value) => <Tag>{value || '-'}</Tag>,
    },
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (value) => value || '-',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (value) => value || '-',
    },
    {
      title: 'Actor',
      key: 'actor',
      render: (_, row) => `${row.actor_type || '-'}${row.actor_id ? ` (${row.actor_id})` : ''}`,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (value) => value || '-',
    },
    {
      title: 'Correlation ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value) => value || '-',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Operational Events</Title>
      <Text type="secondary">Consulta read-only de eventos operacionais estruturados do SaaS Billing.</Text>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Falha ao carregar Operational Events"
          description={error}
        />
      ) : null}

      <Card size="small" title="Filtros" style={{ marginTop: 16 }}>
        <Space wrap>
          <Input
            placeholder="tenant_id"
            value={filters.tenant_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, tenant_id: event.target.value }))}
            style={{ width: 220 }}
          />
          <Input
            placeholder="event_type"
            value={filters.event_type}
            onChange={(event) => setFilters((prev) => ({ ...prev, event_type: event.target.value }))}
            style={{ width: 180 }}
          />
          <Input
            placeholder="event_code"
            value={filters.event_code}
            onChange={(event) => setFilters((prev) => ({ ...prev, event_code: event.target.value }))}
            style={{ width: 220 }}
          />
          <Select
            placeholder="severity"
            options={SEVERITY_OPTIONS}
            value={filters.severity}
            onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
            allowClear
            style={{ width: 160 }}
          />
          <Input
            placeholder="source"
            value={filters.source}
            onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
            style={{ width: 180 }}
          />
          <Input
            placeholder="correlation_id"
            value={filters.correlation_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, correlation_id: event.target.value }))}
            style={{ width: 240 }}
          />
          <DatePicker.RangePicker
            showTime
            value={filters.range}
            onChange={(value) => setFilters((prev) => ({ ...prev, range: value }))}
          />
          <Input
            type="number"
            min={1}
            max={500}
            value={filters.limit}
            onChange={(event) => setFilters((prev) => ({ ...prev, limit: event.target.value }))}
            style={{ width: 120 }}
          />
          <Button type="primary" onClick={fetchEvents} loading={loading}>Aplicar</Button>
          <Button
            onClick={() => {
              setFilters({
                tenant_id: '',
                event_type: '',
                event_code: '',
                severity: undefined,
                source: '',
                correlation_id: '',
                range: null,
                limit: 200,
              });
              setItems([]);
            }}
          >
            Limpar
          </Button>
        </Space>
      </Card>

      <Card size="small" title={`Eventos (${items.length})`} style={{ marginTop: 16 }}>
        <Table
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default PlatformOperationalEventsPage;
